import { CONTACT_LIMITS } from "../src/lib/contact.ts";
import { brandName, contactPhrase, homeLink, loadBrand, pickupLabel } from "./lib/brand.ts";
import { BANK_LABEL, CARD_LABEL, ESTIMATE_CAPTION, TRANSFER_CAPTION } from "../src/lib/estimate.ts";
import { fulfillmentText, TRANSFER_LIMITS } from "../src/lib/transfers.ts";
import type { TransferKind } from "../src/lib/transfers.ts";
import { money } from "../src/lib/money.ts";
import { offsetFields } from "../src/lib/offset.ts";
import { sizeLabel } from "../src/lib/sizes.ts";
import type { Offset } from "../src/lib/offset.ts";
import { SPOTS } from "../src/lib/types.ts";
import type { Spot } from "../src/lib/types.ts";
import type { Category } from "../src/lib/catalog-copy.ts";
import { ApiFailure, api, cropLogo, removeLogoBackground, uploadLogo } from "./lib/api.ts";
import { groupCategories, isStale, onceAtATime, shownCategory, styleCache } from "./lib/catalog.ts";
import type { StyleSummary } from "./lib/catalog.ts";
import type { ProcessedLogo } from "./lib/api.ts";
import { defaultWidth, dpiState, SLIDER_STEP } from "./lib/cart.ts";
import { canCrop, cropChips, croppedArtwork, cropView, keepWidth, openCrop } from "./lib/crop.ts";
import type { CropRect, CropSession } from "./lib/crop.ts";
import { savePrefill } from "./lib/checkout.ts";
import {
  breakHint, colorCount, colorsMissingQuantities, contactErrors, designDpiNotice, designRequestBody, estimateBody, gangMessage, pieceBounds,
  ownDesignGarment, printLines, shownUnit, transferBreakHint, transferDpi, transferDpiNotice, transferEstimateBody, transferQuantityOk, transferRequestBody, transferSize, unitLines,
} from "./lib/design.ts";
import type { CatalogTier, Contact, ContactErrors, DesignGarment, Dual, Estimate, Product, SizedPlacement, TransferEstimate, TransferPick } from "./lib/design.ts";
import { blankShippingView, discountView } from "./lib/discount.ts";
import { h, mount } from "./lib/dom.ts";
import { measureLogoPixels } from "./lib/logo-pixels.ts";
import {
  arrowNudge, bindDrag, chooseOffset, displayedPxPerIn, dragOffset, focusAfterNudge, focusAfterReset, movableOverlay, nudgeDisabled, nudgeOffset,
  positionControls, positionLine, showPosition, shownOffset, ZERO_OFFSET,
} from "./lib/offset-ui.ts";
import type { Nudge } from "./lib/offset-ui.ts";
import { photoCache } from "./lib/photos.ts";
import { applyColorFilter, revealChosenChip, stylePicker } from "./lib/picker.ts";
import { TURNAROUND } from "../src/lib/turnaround.ts";
import { garmentsBlock, GARMENTS_TITLE, OWN_BUTTON, OWN_COLORS, OWN_TYPES } from "../src/lib/own-garments.ts";
import type { OwnGarment, OwnType } from "../src/lib/own-garments.ts";
import { placeOverlay } from "./lib/preview.ts";
import { fittedWidths, INK_LABEL, inkAllowed, inkNotice, INKS, listWords, sizeAt, SPOT_LABEL, spotBounds, spotLimits, usableSpots } from "./lib/studio.ts";
import type { Ink } from "./lib/studio.ts";

// The styles only; a style's colors come from the `style` action when it is picked.
interface Catalog {
  styles: StyleSummary[];
  // Each print's two prices by its size; the page words them as the server does (printLines).
  printTiers: CatalogTier[];
  rules: { printerMaxWidthIn: number; maxLengthIn: number };
  limits: { maxColors: number; maxPieces: number; maxGangSheets: number };
}
interface Sent {
  token: string | null;
  shareUrl: string | null;
  estimate: Dual;
  emailed: boolean;
  orderable: boolean;
  // Where each placement's logo sits, in words, as it was sent (apparel only; the page adds it).
  positions?: string[];
}
type Busy = "photos" | "upload" | "cutout" | "crop" | "send";
type Side = "front" | "back";

const scope = { design: true as const };
const app = document.getElementById("app")!;
const NARROW = "(max-width: 860px)";
const FRONT: Spot[] = ["front-center", "left-chest"];
const SPLIT = "Send those colors as a separate design.";
// Signed photo links last 600 s: a picked color's photos are held as blob: URLs, and the catalog and the style's colors
// are fetched again when they are older than STALE_MS (8 minutes).
const heldPhotos = photoCache();
let loadedAt = 0;
let styleLoadedAt = 0;
// Each style's answer, kept until it goes stale.
const styles = styleCache<DesignGarment[]>();

const s = {
  catalog: null as Catalog | null,
  // The category chip picked (null: the picked style's, else the first).
  category: null as Category | null,
  style: null as string | null,
  // The picked style's colors, with their photos; null until loaded.
  colors: null as DesignGarment[] | null,
  // The "Find a color" box.
  filter: "",
  picked: [] as string[],
  // The color the preview shows; the first picked one when unset.
  showing: null as string | null,
  colorNote: "",
  uploaded: null as { original: ProcessedLogo; cutout?: ProcessedLogo } | null,
  logo: null as ProcessedLogo | null,
  // The cropper while it is open, and the artwork as uploaded once a crop is applied (Reset to the original).
  cropping: null as CropSession | null,
  beforeCrop: null as { uploaded: { original: ProcessedLogo; cutout?: ProcessedLogo }; logo: ProcessedLogo } | null,
  logoTone: null as "light" | "dark" | null,
  logoShare: null as number | null,
  ink: "original" as Ink,
  on: { "front-center": true, "left-chest": false, "full-back": false } as Record<Spot, boolean>,
  width: { "front-center": 0, "left-chest": 0, "full-back": 0 } as Record<Spot, number>,
  // Where the buyer moved each logo within its spot (zero is the standard position).
  offset: { "front-center": ZERO_OFFSET, "left-chest": ZERO_OFFSET, "full-back": ZERO_OFFSET } as Record<Spot, Offset>,
  photos: {} as Partial<Record<Side, { w: number; h: number }>>,
  quantities: {} as Record<string, Record<string, number>>,
  estimate: null as Estimate | null,
  estimateState: "idle" as "idle" | "loading" | "ready" | "failed",
  // Printed apparel, DTF transfers or the buyer's own garments: one product per request. The page starts on apparel.
  product: "apparel" as Product,
  // The buyer's own garments: the type they send and the color the preview shows (black until picked).
  ownType: null as OwnType | null,
  ownColor: "black",
  kind: "piece" as TransferKind,
  pieceWidth: 0,
  transferQty: 0,
  fulfillment: "pickup" as "pickup" | "ship",
  transferEstimate: null as TransferEstimate | null,
  form: { name: "", email: "", phone: "", business: "", note: "" } as Contact,
  formErrors: {} as ContactErrors,
  // The field people never see (see contactStep); it is sent as `website`, the key the server checks.
  honeypot: "",
  // Made when Send is first pressed for this design; any change clears it, so a retry after an error is one request.
  requestId: null as string | null,
  sent: null as Sent | null,
  copied: false,
  busy: new Set<Busy>(),
  logoError: "",
  sendError: "",
};

const locked = () => s.busy.size > 0;
// The contact fields in page order, for focusing the first one with an error.
const CONTACT_FIELDS: (keyof Contact)[] = ["name", "email", "phone", "business", "note"];
const message = (err: unknown) => (err instanceof ApiFailure ? err.message : "Something went wrong. Please try again.");
const section = (title: string, ...children: (Node | string | null | false | undefined)[]) => h("section", { class: "step" }, h("h2", {}, title), ...children);
// The buyer's own garments are one "color": the silhouette for the type picked.
const ownPick = (): OwnGarment | null => (s.product === "own" && s.ownType ? { garmentType: s.ownType, color: s.ownColor } : null);
let ownShown: { key: string; g: DesignGarment } | null = null;
function ownGarment(o: OwnGarment): DesignGarment {
  const key = `${o.garmentType}:${o.color}`;
  if (ownShown?.key !== key) ownShown = { key, g: ownDesignGarment(o) };
  return ownShown.g;
}
const picked = (): DesignGarment[] => {
  if (s.product === "own") {
    const o = ownPick();
    return o ? [ownGarment(o)] : [];
  }
  return s.picked.map((id) => s.colors?.find((g) => g.id === id)).filter((g): g is DesignGarment => g !== undefined);
};
const shown = (): DesignGarment | undefined => picked().find((g) => g.id === s.showing) ?? picked()[0];
const maxWidth = () => s.catalog!.rules.printerMaxWidthIn;
const logoSize = () => (s.logo ? { widthPx: s.logo.widthPx, heightPx: s.logo.heightPx } : null);
const currentSize = (spot: Spot) => sizeAt(picked(), logoSize(), spot, s.width[spot], maxWidth());
// Where a spot's logo shows: the buyer's choice kept inside the tightest spot across the picked colors at the fitted
// size, as the server clamps it; zero while the spot cannot be placed. s.offset keeps the choice itself.
const fitOffset = (spot: Spot): Offset => shownOffset(spotLimits(picked(), spot), currentSize(spot), s.offset[spot]);
const film = () => ({ printerMaxWidthIn: s.catalog!.rules.printerMaxWidthIn, maxLengthIn: s.catalog!.rules.maxLengthIn });
// A piece has no size until fitForLogo picks its width (the cutout step renders before that).
const currentTransfer = () => (s.logo && (s.kind === "gang" || s.pieceWidth > 0) ? transferSize(s.kind, s.logo, s.pieceWidth, film()) : null);
// The size and DPI the buyer reads: the server's once its estimate is in (it sizes from the stored artwork), else
// the page's own.
function shownTransfer(): { size: { widthIn: number; heightIn: number }; dpi: number } | null {
  const e = s.estimateState === "ready" ? s.transferEstimate : null;
  if (e) return { size: e.size, dpi: e.dpi };
  const size = currentTransfer();
  return s.logo && size ? { size, dpi: transferDpi(s.kind, s.logo, size) } : null;
}
// The transfer order as it stands, or null while it cannot be priced or sent (no art, blurry art, the wrong shape
// for a gang sheet, or a quantity out of range).
function transferPick(): TransferPick | null {
  const logo = s.logo;
  const size = currentTransfer();
  if (!logo || !size || !transferQuantityOk(s.kind, s.transferQty)) return null;
  if (s.kind === "gang" && gangMessage(logo, film()) !== null) return null;
  if (transferDpi(s.kind, logo, size) < TRANSFER_LIMITS.minDpi) return null;
  return { logoId: logo.logoId, kind: s.kind, widthIn: s.pieceWidth, quantity: s.transferQty, fulfillment: s.fulfillment };
}
const wrongShape = () => s.product === "transfers" && s.kind === "gang" && s.logo !== null && gangMessage(s.logo, film()) !== null;
// Why the artwork cannot be priced or sent as it stands (the wrong shape for a gang sheet, or too few pixels for its
// size), or null when it can. The estimate pane and the Send hint each finish the sentence.
function transferArtBlock(): string | null {
  const logo = s.logo;
  const size = currentTransfer();
  if (!logo || !size) return null;
  if (wrongShape()) return "Upload a gang sheet in the film's shape";
  if (transferDpi(s.kind, logo, size) < TRANSFER_LIMITS.minDpi) return "Upload artwork that prints well at this size";
  return null;
}
// The estimate the page may show: none while a newer one loads, so an old price never stands for a changed order.
const liveEstimate = () => (s.estimateState === "loading" ? null : s.estimate);
// The placements turned on, at the sizes the page shows (the server sizes them the same way from the same widths).
const placedSizes = (): SizedPlacement[] => SPOTS.flatMap((spot) => {
  const size = s.on[spot] ? currentSize(spot) : null;
  return size ? [{ spot, widthIn: size.widthIn, heightIn: size.heightIn }] : [];
});
// What the estimate prices the prints from: the artwork and each placement's width (where it sits does not change the
// price, so a move asks for nothing).
const estimateArt = () => (s.logo ? { logoId: s.logo.logoId, placements: placedSizes().map((p) => ({ spot: p.spot, widthIn: p.widthIn })) } : null);
// Apparel is ready once every picked color has a garment, so Send is never offered only to refuse.
const apparelReady = () => picked().length > 0 && Boolean(s.logo) && SPOTS.some((sp) => s.on[sp]) && colorsMissingQuantities(picked(), s.quantities).length === 0;
const ready = () => (s.product === "transfers" ? transferPick() !== null : apparelReady());

// What Send is waiting for, or null when it is ready.
function sendHint(): string | null {
  if (ready()) return null;
  if (s.product === "transfers") {
    if (!s.logo) return "Upload your artwork and enter a quantity first.";
    const blocked = transferArtBlock();
    return blocked ? `${blocked} to send it.` : "Enter a quantity first.";
  }
  const missing = picked().length > 0 && s.logo ? colorsMissingQuantities(picked(), s.quantities) : [];
  if (s.product === "own") {
    return missing.length > 0 ? "Enter how many garments you are sending first." : "Pick your garment type, upload your artwork, turn on a placement and enter quantities first.";
  }
  return missing.length > 0 ? `Add at least one garment for ${listWords(missing)}, or remove ${missing.length === 1 ? "that color" : "those colors"}.`
    : "Pick colors, upload your artwork, turn on a placement and enter quantities first.";
}

function fitWidths(reset = false) {
  s.width = fittedWidths(picked(), logoSize(), s.width, maxWidth(), reset);
  // A new logo starts at the standard position, as in the Studio.
  if (reset) s.offset = { "front-center": ZERO_OFFSET, "left-chest": ZERO_OFFSET, "full-back": ZERO_OFFSET };
}
function fixSpots() {
  s.on = usableSpots(s.on, picked());
}
// Anything that changes what would be sent makes the next Send a new request, and takes away an old Send error
// (typing and sliders do not rebuild the page, so its line is removed here).
function changed() {
  s.requestId = null;
  if (!s.sendError) return;
  s.sendError = "";
  document.getElementById("send-error")?.remove();
}

async function measureLogo() {
  const logo = s.logo;
  s.logoTone = null;
  s.logoShare = null;
  if (!logo) return;
  const m = await measureLogoPixels(logo.previewUrl);
  if (s.logo !== logo) return;
  s.logoTone = m?.tone ?? null;
  s.logoShare = m?.share ?? null;
  if (!inkOk() && s.ink !== "original") {
    s.ink = "original";
    changed();
  }
  render();
}
// The white box was found and the buyer kept it: the art is a solid rectangle, whatever its pixels say.
function keptBackground() {
  const u = s.uploaded;
  return Boolean(u && s.logo === u.original && u.original.backgroundDetected);
}
const inkOk = () => !keptBackground() && inkAllowed(s.logoShare);

async function loadCatalog() {
  s.catalog = await api<Catalog>("/api/design", { body: { action: "catalog" } });
  loadedAt = Date.now();
}

// One style's colors, from the page's copy while it is fresh (force asks the server); photos already held (picked
// colors) keep their blob: URLs.
async function loadStyle(key: string, force = false) {
  const kept = force ? null : styles.get(key, Date.now());
  let garments = kept?.value;
  let at = kept?.at ?? 0;
  if (!garments) {
    garments = (await api<{ style: string; garments: DesignGarment[] }>("/api/design", { body: { action: "style", style: key } })).garments;
    at = Date.now();
    styles.set(key, garments, at);
  }
  heldPhotos.reuse(garments);
  if (s.style !== key) return;
  s.colors = garments;
  styleLoadedAt = at;
}

// A swatch failed to load: when the links are old, fetch the style again (once at a time) and redraw. Read-only, so it
// runs while a request is in flight too.
const refreshStaleStyle = onceAtATime(async () => {
  const key = s.style;
  if (!key || !isStale(styleLoadedAt, Date.now())) return;
  try {
    await loadStyle(key, true);
    render();
  } catch {
    // The swatch stays blank; picking the style again fetches it.
  }
});

// A style card's swatch failed to load: when the catalog is old, fetch it again (once at a time) and redraw.
const refreshStaleCatalog = onceAtATime(async () => {
  if (!isStale(loadedAt, Date.now())) return;
  try {
    await loadCatalog();
    render();
  } catch {
    // The card keeps its blank swatch; the next category or style pick tries again.
  }
});

// Holds a picked color's photos; when they do not load the buyer hears so (the preview may stay blank).
async function holdPicked(g: DesignGarment) {
  const failed = await heldPhotos.hold([g]).catch(() => [g.id]);
  if (failed.length === 0 || !s.picked.includes(g.id)) return;
  s.colorNote = `The photo of ${g.color} did not load. Unpick it and pick it again to retry.`;
  render();
}

// ---- the estimate: asked of the server 500 ms after the last change; an older answer arriving late is dropped ----

let estimateSeq = 0;
let estimateTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleEstimate() {
  clearTimeout(estimateTimer);
  const seq = ++estimateSeq;
  const product = s.product;
  const pick = product === "transfers" ? transferPick() : null;
  const body = product === "transfers" ? (pick ? transferEstimateBody(pick) : null) : estimateBody(picked(), s.quantities, estimateArt(), ownPick());
  if (!body) {
    s.estimate = null;
    s.transferEstimate = null;
    s.estimateState = "idle";
    refreshEstimate();
    return;
  }
  s.estimateState = "loading";
  refreshEstimate();
  estimateTimer = setTimeout(async () => {
    try {
      if (product === "transfers") {
        const e = await api<TransferEstimate>("/api/design", { body });
        if (seq !== estimateSeq) return;
        s.transferEstimate = e;
      } else {
        const e = await api<Estimate>("/api/design", { body });
        if (seq !== estimateSeq) return;
        s.estimate = e;
      }
      s.estimateState = "ready";
    } catch {
      if (seq !== estimateSeq) return;
      s.estimate = null;
      s.transferEstimate = null;
      s.estimateState = "failed";
    }
    refreshEstimate();
  }, 500);
}

const countText = (g: DesignGarment) => {
  const n = colorCount(s.quantities[g.id]);
  return n > 0 ? `${n} ${n === 1 ? "garment" : "garments"}` : "No garments yet.";
};

// Swaps in the estimate pane and the size table's prices and counts without rebuilding the page, so typing keeps its
// place in the quantity boxes.
function refreshEstimate() {
  // The pane itself stays (it is the one live region for the estimate); only what is inside it changes.
  const fresh = s.product === "transfers" ? transferEstimatePane() : estimatePane();
  document.getElementById("estimate")?.replaceChildren(...Array.from(fresh.childNodes));
  if (s.product === "transfers") return updateTransferPreview();
  refreshSend();
  for (const g of picked()) {
    const count = document.getElementById(`count-${g.id}`);
    if (count) count.textContent = countText(g);
    for (const size of g.sizes) {
      const [bank, card] = unitLines(shownUnit(g, size, liveEstimate()));
      const b = document.getElementById(`bank-${g.id}-${size}`);
      if (b) b.textContent = bank;
      const c = document.getElementById(`card-${g.id}-${size}`);
      if (c) c.textContent = card;
    }
  }
}

// Send's state follows the order without a rebuild (sliders and quantity boxes keep focus).
function refreshSend() {
  const button = document.getElementById("send") as HTMLButtonElement | null;
  if (button) button.disabled = locked() || !ready();
  const hint = document.getElementById("send-hint");
  if (hint) {
    const text = sendHint();
    hint.hidden = text === null;
    hint.textContent = text ?? "";
  }
}

// Picking a product starts it fresh, so a request can never carry both (the server refuses a mix too). The buyer's
// contact details stay.
function pickProduct(p: Product) {
  if (locked() || p === s.product) return;
  clearTimeout(estimateTimer);
  estimateSeq++;
  Object.assign(s, {
    product: p, style: null, colors: null, filter: "", picked: [], showing: null, quantities: {}, estimate: null, transferEstimate: null, estimateState: "idle",
    uploaded: null, logo: null, cropping: null, beforeCrop: null, logoTone: null, logoShare: null, ink: "original", logoError: "", colorNote: "",
    on: { "front-center": true, "left-chest": false, "full-back": false }, width: { "front-center": 0, "left-chest": 0, "full-back": 0 },
    offset: { "front-center": ZERO_OFFSET, "left-chest": ZERO_OFFSET, "full-back": ZERO_OFFSET },
    kind: "piece", pieceWidth: 0, transferQty: 0, fulfillment: "pickup", ownType: null, ownColor: "black",
  });
  changed();
  render();
}

// The buyer's own garments: the type sets the sizes and the spots, so quantities start over; the color only repaints.
function pickOwnType(t: OwnType) {
  if (locked() || t === s.ownType) return;
  s.ownType = t;
  s.quantities = {};
  s.photos = {};
  fixSpots();
  fitWidths();
  changed();
  scheduleEstimate();
  render();
}

function pickOwnColor(key: string) {
  if (locked() || key === s.ownColor) return;
  s.ownColor = key;
  s.photos = {};
  render();
}

// A gang sheet is uploaded differently (kept whole), so switching kinds asks for the artwork again.
function pickKind(k: TransferKind) {
  if (locked() || k === s.kind) return;
  const max = k === "gang" ? s.catalog!.limits.maxGangSheets : s.catalog!.limits.maxPieces;
  Object.assign(s, { kind: k, uploaded: null, logo: null, cropping: null, beforeCrop: null, logoError: "", pieceWidth: 0, transferEstimate: null, transferQty: Math.min(s.transferQty, max) });
  changed();
  scheduleEstimate();
  render();
}

function setFulfillment(f: "pickup" | "ship") {
  s.fulfillment = f;
  changed();
  scheduleEstimate();
}

// After the artwork changes: fresh slider widths for apparel, a fresh width for a transfer piece.
function fitForLogo() {
  if (s.product === "transfers") s.pieceWidth = s.logo ? defaultWidth(pieceBounds(s.logo, film()), s.logo.widthPx) : 0;
  else fitWidths(true);
  scheduleEstimate();
}

// ---- actions ----

// The style cards' swatches are signed links too: an old catalog is fetched again before another category shows.
async function pickCategory(c: Category) {
  if (locked()) return;
  s.category = c;
  if (isStale(loadedAt, Date.now())) {
    s.busy.add("photos");
    render();
    try {
      await loadCatalog();
    } catch (err) {
      s.colorNote = message(err);
    }
    s.busy.delete("photos");
  }
  render();
}

async function pickStyle(key: string) {
  if (locked() || key === s.style) return;
  s.style = key;
  s.colors = null;
  s.filter = "";
  s.picked = [];
  s.showing = null;
  s.quantities = {};
  s.colorNote = "";
  changed();
  scheduleEstimate();
  s.busy.add("photos");
  render();
  try {
    if (isStale(loadedAt, Date.now())) await loadCatalog();
    await loadStyle(key);
  } catch (err) {
    s.colorNote = message(err);
  }
  s.busy.delete("photos");
  render();
  // On a phone the colors sit under the style cards: bring them up.
  if (s.colors && matchMedia(NARROW).matches) document.getElementById("swatches-title")?.scrollIntoView({ block: "start" });
}

async function toggleColor(g: DesignGarment, input: HTMLInputElement) {
  if (locked()) return;
  const checked = input.checked;
  const max = s.catalog!.limits.maxColors;
  s.colorNote = "";
  if (checked) {
    if (s.picked.length >= max) {
      input.checked = false;
      s.colorNote = `Pick at most ${max} colors. Send more as a separate design.`;
      return render();
    }
    // The preview needs this color's full photo: old links are fetched again first. If that fails nothing is picked,
    // and after the wait the pick is checked again (the style or the picks may have changed meanwhile).
    if (isStale(styleLoadedAt, Date.now()) && s.style) {
      s.busy.add("photos");
      render();
      try {
        await loadStyle(s.style, true);
      } catch (err) {
        s.colorNote = message(err);
      }
      s.busy.delete("photos");
      if (s.colorNote || !s.colors?.some((x) => x.id === g.id) || s.picked.includes(g.id) || s.picked.length >= max) return render();
    }
    s.picked.push(g.id);
  } else {
    s.picked = s.picked.filter((id) => id !== g.id);
    if (s.showing === g.id) s.showing = null;
  }
  fixSpots();
  fitWidths();
  changed();
  scheduleEstimate();
  render();
  // Held as blob: URLs in the background, so the preview outlives the signed links. Only picked colors load the full
  // photo; the swatches are the small thumbs.
  const held = picked().find((x) => x.id === g.id);
  if (held) void holdPicked(held);
}

async function onFile(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file || locked()) return;
  const gang = s.product === "transfers" && s.kind === "gang";
  s.busy.add("upload");
  s.logoError = "";
  render();
  try {
    // A gang sheet is printed as laid out: no background removal, and its margins are kept.
    const original = await uploadLogo(scope, file, gang ? { gangSheet: true } : {});
    const u: { original: ProcessedLogo; cutout?: ProcessedLogo } = { original };
    s.uploaded = u;
    s.logo = original;
    s.cropping = null;
    s.beforeCrop = null;
    s.ink = "original";
    // Background removal is on by default; Keep background switches back.
    if (original.backgroundDetected && !gang) {
      s.busy.add("cutout");
      render();
      try {
        u.cutout = await removeLogoBackground(scope, original.logoId);
        s.logo = u.cutout;
      } catch (err) {
        s.logoError = message(err);
      }
      s.busy.delete("cutout");
    }
    fitForLogo();
    changed();
    void measureLogo();
  } catch (err) {
    s.logoError = message(err);
  }
  s.busy.delete("upload");
  render();
}

async function useCutout() {
  const u = s.uploaded;
  if (!u || locked()) return;
  if (!u.cutout) {
    s.busy.add("cutout");
    s.logoError = "";
    render();
    try {
      u.cutout = await removeLogoBackground(scope, u.original.logoId);
    } catch (err) {
      s.logoError = message(err);
    }
    s.busy.delete("cutout");
  }
  if (u.cutout) {
    s.logo = u.cutout;
    fitForLogo();
    changed();
    void measureLogo();
  }
  render();
}

function useOriginal() {
  const u = s.uploaded;
  if (!u || locked()) return;
  s.logo = u.original;
  s.ink = "original";
  fitForLogo();
  changed();
  void measureLogo();
  render();
}

function startCrop() {
  if (locked() || !s.logo) return;
  s.cropping = openCrop(s.logo);
  render();
}

function cancelCrop() {
  if (locked()) return;
  s.cropping = null;
  render();
}

// After a crop or a reset: the widths the buyer chose stay where they still fit (the offsets are kept inside the spot
// as they show), as a transfer piece's width does.
function refitForLogo() {
  if (s.product === "transfers") {
    s.pieceWidth = s.logo ? (s.pieceWidth > 0 ? keepWidth(s.pieceWidth, pieceBounds(s.logo, film())) : defaultWidth(pieceBounds(s.logo, film()), s.logo.widthPx)) : 0;
  } else fitWidths();
  scheduleEstimate();
}

async function applyCrop(rect: CropRect) {
  const from = s.logo;
  const u = s.uploaded;
  if (!from || !u || locked() || s.cropping?.logoId !== from.logoId) return;
  s.busy.add("crop");
  s.logoError = "";
  render();
  try {
    const next = croppedArtwork(await cropLogo(scope, from.logoId, rect));
    s.beforeCrop ??= { uploaded: u, logo: from };
    s.uploaded = next;
    s.logo = next.original;
    s.cropping = null;
    if (!inkOk()) s.ink = "original";
    refitForLogo();
    changed();
    void measureLogo();
  } catch (err) {
    s.logoError = message(err);
  }
  s.busy.delete("crop");
  render();
}

function resetCrop() {
  const b = s.beforeCrop;
  if (!b || locked()) return;
  Object.assign(s, { uploaded: b.uploaded, logo: b.logo, beforeCrop: null, cropping: null, ink: "original" });
  refitForLogo();
  changed();
  void measureLogo();
  render();
}

function pickInk(ink: Ink) {
  if (locked() || ink === s.ink || (ink !== "original" && !inkOk())) return;
  s.ink = ink;
  changed();
  render();
}

function toggleSpot(spot: Spot, input: HTMLInputElement) {
  if (locked()) return;
  if (!input.checked && SPOTS.filter((sp) => s.on[sp]).length === 1) {
    input.checked = true;
    return;
  }
  s.on[spot] = input.checked;
  changed();
  scheduleEstimate();
  render();
}

// Whole numbers from 0 to max; a box holding anything else is set to what was kept (an empty box stays empty).
function clampQuantity(input: HTMLInputElement, max: number): number {
  const n = Math.min(max, Math.max(0, Math.floor(Number(input.value)) || 0));
  if (input.value !== "" && Number(input.value) !== n) input.value = n ? String(n) : "";
  return n;
}

function setQuantity(g: DesignGarment, size: string, input: HTMLInputElement) {
  const n = clampQuantity(input, 999);
  s.quantities[g.id] = { ...(s.quantities[g.id] ?? {}), [size]: n };
  changed();
  scheduleEstimate();
}

// Moves a logo within its spot without rebuilding the page, so the control in use keeps focus.
function moveLogo(spot: Spot, o: Offset) {
  const next = chooseOffset(spotLimits(picked(), spot), currentSize(spot), s.offset[spot], o);
  if (!next) return;
  s.offset[spot] = next;
  changed();
  updatePreview();
}

function nudge(spot: Spot, dir: Nudge) {
  const size = currentSize(spot);
  const limits = spotLimits(picked(), spot);
  if (locked() || !size || !limits) return;
  moveLogo(spot, nudgeOffset(limits, size, fitOffset(spot), dir));
  focusAfterNudge(document, spot, dir);
}

function resetPosition(spot: Spot) {
  if (locked()) return;
  s.offset[spot] = ZERO_OFFSET;
  changed();
  updatePreview();
  focusAfterReset(document, spot);
}

// The overlay on the preview follows the pointer: the drag is measured in screen pixels and turned into inches by the
// photo's shown width and the garment's ppi.
function makeMovable(overlay: HTMLElement, spot: Spot, side: Side) {
  let start: { offset: Offset; pxPerIn: number } | null = null;
  bindDrag(overlay, {
    begin() {
      const g = shown();
      const photo = overlay.parentElement?.querySelector<HTMLImageElement>("img.photo");
      const natural = s.photos[side];
      if (locked() || !g || !photo || !natural || !currentSize(spot)) return false;
      const pxPerIn = displayedPxPerIn(photo.getBoundingClientRect().width, natural.w, g.ppi);
      if (!(pxPerIn > 0)) return false;
      overlay.focus({ preventScroll: true });
      start = { offset: fitOffset(spot), pxPerIn };
      overlay.classList.add("dragging");
      return true;
    },
    move(dx, dy) {
      const size = currentSize(spot);
      const limits = spotLimits(picked(), spot);
      if (start && size && limits) moveLogo(spot, dragOffset(limits, size, start.offset, dx, dy, start.pxPerIn));
    },
    end() {
      start = null;
      overlay.classList.remove("dragging");
    },
  });
  overlay.addEventListener("keydown", (e: KeyboardEvent) => {
    const dir = arrowNudge(e.key);
    if (!dir) return;
    e.preventDefault();
    nudge(spot, dir);
  });
}

// The apparel request, or null with s.sendError saying what is missing.
function apparelBody(requestId: string, logo: ProcessedLogo): Record<string, unknown> | null {
  const list = picked();
  const placements = SPOTS.filter((sp) => s.on[sp]).map((sp) => ({ spot: sp, size: currentSize(sp) }));
  const missing = colorsMissingQuantities(list, s.quantities);
  s.sendError = placements.length === 0 || placements.some((p) => !p.size)
    ? "Turn on at least one placement."
    : missing.length > 0 ? `Add at least one garment for ${listWords(missing)}, or remove ${missing.length === 1 ? "that color" : "those colors"}.` : "";
  if (s.sendError) return null;
  return designRequestBody({
    requestId, logoId: logo.logoId, garments: list, ink: s.ink, placements: placements.map((p) => ({ spot: p.spot, widthIn: p.size!.widthIn, ...offsetFields(fitOffset(p.spot)) })),
    quantities: s.quantities, contact: s.form, website: s.honeypot, own: ownPick(),
  });
}

// The transfers request, or null with s.sendError saying what is missing.
function transferBody(requestId: string): Record<string, unknown> | null {
  const pick = transferPick();
  s.sendError = pick ? "" : sendHint() ?? "";
  return pick ? transferRequestBody({ ...pick, requestId, contact: s.form, website: s.honeypot }) : null;
}

async function send(e: Event) {
  e.preventDefault();
  const logo = s.logo;
  if (locked() || !logo) return;
  s.formErrors = contactErrors(s.form);
  // Made once per design: a retry after an error sends the same id, so the server makes one request of it.
  const requestId = s.requestId ?? crypto.randomUUID();
  const body = s.product === "transfers" ? transferBody(requestId) : apparelBody(requestId, logo);
  if (Object.keys(s.formErrors).length > 0 || !body) {
    render();
    // Focus goes to the first field to fix, which reads out its error.
    const first = CONTACT_FIELDS.find((k) => s.formErrors[k]);
    if (first) document.getElementById(`contact-${first}`)?.focus();
    return;
  }
  s.requestId = requestId;
  s.busy.add("send");
  render();
  try {
    const sent = await api<Sent>("/api/design", { body });
    const positions = s.product !== "transfers" ? SPOTS.filter((sp) => s.on[sp]).map((sp) => positionLine(SPOT_LABEL[sp], fitOffset(sp))) : undefined;
    s.sent = { ...sent, positions };
  } catch (err) {
    s.sendError = message(err);
  }
  s.busy.delete("send");
  render();
  if (s.sent) {
    window.scrollTo({ top: 0 });
    document.getElementById("sent-title")?.focus({ preventScroll: true });
  }
}

async function copyLink() {
  const url = s.sent?.shareUrl;
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    s.copied = true;
    // Changed in place, not rebuilt, so the button keeps focus.
    const button = document.getElementById("copy-link");
    if (button) button.textContent = "Copied";
  } catch {
    (document.getElementById("share-url") as HTMLInputElement | null)?.select();
  }
}

// ---- views ----

function styleStep(c: Catalog) {
  if (c.styles.length === 0) {
    return section("1. Style and colors", notReady());
  }
  return section("1. Style and colors",
    ...stylePicker({
      styles: c.styles, category: shownCategory(groupCategories(c.styles), s.category, s.style), style: s.style, colors: s.colors, picked: s.picked,
      filter: s.filter, locked: locked(), colorLabel: (g) => g.color,
      onCategory: (c) => void pickCategory(c), onCardError: () => void refreshStaleCatalog(), onStyle: (key) => void pickStyle(key), onToggle: (g, input) => void toggleColor(g, input),
      onFilter: (text) => { s.filter = text; }, onThumbError: () => void refreshStaleStyle(),
    }),
    s.colors && h("p", { class: "muted" }, `${s.picked.length} of ${c.limits.maxColors} colors picked.`),
    s.colorNote && h("p", { class: "notice warn", "aria-live": "polite" }, s.colorNote));
}

// Remove / Keep background (when a background was found) and Crop, shared by apparel artwork and transfer pieces
// (never offered for a gang sheet, which prints exactly as laid out).
function cutoutChips(u: { original: ProcessedLogo; cutout?: ProcessedLogo }, usingCutout: boolean) {
  const keeping = s.logo?.logoId === u.original.logoId;
  const crop = cropChips({ canCrop: Boolean(s.logo && canCrop(s.logo)), cropped: s.beforeCrop !== null, locked: locked(), onCrop: startCrop, onReset: resetCrop });
  if (!u.original.backgroundDetected && crop.length === 0) return null;
  return h("div", { class: "row" },
    u.original.backgroundDetected && h("button", { id: "logo-cutout", type: "button", class: `chip${usingCutout ? " on" : ""}`, "aria-pressed": String(usingCutout), disabled: locked(), onclick: useCutout },
      s.busy.has("cutout") ? "Removing..." : "Remove background"),
    u.original.backgroundDetected && h("button", { id: "logo-original", type: "button", class: `chip${keeping ? " on" : ""}`, "aria-pressed": String(keeping), disabled: locked(), onclick: useOriginal },
      "Keep background"),
    ...crop);
}

// The artwork as it shows, or the cropper over it while one is open.
function artworkTile(u: { original: ProcessedLogo; cutout?: ProcessedLogo }, usingCutout: boolean) {
  const logo = usingCutout ? u.cutout! : u.original;
  if (s.cropping && s.cropping.logoId === s.logo?.logoId) {
    return cropView(s.cropping, { src: s.logo.previewUrl, busy: s.busy.has("crop"), locked: locked(), onApply: (r) => void applyCrop(r), onCancel: cancelCrop });
  }
  return h("img", { class: "checker", src: logo.previewUrl, alt: "Your artwork" });
}

function artStep() {
  const u = s.uploaded;
  const usingCutout = Boolean(u?.cutout && s.logo?.logoId === u.cutout.logoId);
  const canInk = inkOk();
  // A kept white box makes the mean read light whatever the mark is, so no tone warning then.
  const clash = s.logo ? inkNotice(keptBackground() ? null : s.logoTone, s.ink, picked(), canInk, SPLIT) : null;
  return section("2. Artwork",
    h("label", { class: "drop" },
      s.busy.has("upload") ? "Checking the file..." : "Upload your artwork: PNG, JPG or SVG, up to 25 MB",
      h("input", { id: "logo-file", type: "file", accept: ".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml", disabled: locked(), onchange: onFile })),
    u && h("div", { class: "uploaded" },
      artworkTile(u, usingCutout),
      !s.cropping && cutoutChips(u, usingCutout)),
    s.logo && h("div", { class: "row ink", role: "group", "aria-label": "Ink" },
      h("span", { class: "muted" }, "Ink:"),
      ...INKS.map((ink) => h("button", { id: `ink-${ink}`, type: "button", class: `chip${s.ink === ink ? " on" : ""}`, "aria-pressed": String(s.ink === ink),
        disabled: locked() || (ink !== "original" && !canInk), onclick: () => pickInk(ink) }, INK_LABEL[ink]))),
    s.logo && !canInk && h("p", { class: "muted" }, "One-color ink needs the background removed."),
    clash && h("p", { class: "notice warn", "aria-live": "polite" }, clash),
    s.logoError && h("p", { class: "notice bad", "aria-live": "polite" }, s.logoError));
}

function placeStep() {
  const list = picked();
  return section("3. Placements",
    ...SPOTS.map((spot) => {
      const available = spotLimits(list, spot) !== null;
      const b = s.on[spot] ? spotBounds(list, logoSize(), spot, maxWidth()) : null;
      return h("div", { class: "placement" },
        h("label", { class: "check" },
          h("input", { id: `spot-${spot}`, type: "checkbox", checked: s.on[spot], disabled: locked() || !available, onchange: (e: Event) => toggleSpot(spot, e.target as HTMLInputElement) }),
          h("span", {}, available ? SPOT_LABEL[spot] : `${SPOT_LABEL[spot]} (not on every color picked)`)),
        b && h("label", { class: "field" },
          h("span", { id: `size-${spot}` }),
          h("input", { id: `width-${spot}`, type: "range", min: String(b.min), max: String(b.max), step: String(SLIDER_STEP), value: String(s.width[spot]),
            disabled: locked(), "aria-labelledby": `size-${spot}`,
            // The page is not rebuilt while dragging, so the slider keeps focus.
            oninput: (e: Event) => { s.width[spot] = Number((e.target as HTMLInputElement).value); changed(); updatePreview(); scheduleEstimate(); } })),
        b && h("p", { id: `dpi-${spot}`, class: "notice", "aria-live": "polite" }),
        b && positionControls(spot, SPOT_LABEL[spot], fitOffset(spot), locked(), { nudge: (dir) => nudge(spot, dir), reset: () => resetPosition(spot) }));
    }));
}

function previewPane() {
  const list = picked();
  const g = shown();
  if (!g) return h("div", { class: "stage empty" }, s.product === "own" ? "Pick your garment type to see the preview." : "Pick a color to see the preview.");
  const logo = s.logo;
  const ink = s.ink === "original" ? "" : ` ink-${s.ink}`;
  const tag = (side: Side) => (side === "front" ? "Front" : g.backPhotoUrl ? "Back" : "Back, shown on the front photo");
  const stage = (side: Side, src: string, spots: Spot[]) => h("div", { class: "stage" },
    h("img", { class: "photo", src, alt: `${g.name}, ${g.color}, ${side}`,
      onload: (e: Event) => { const img = e.target as HTMLImageElement; s.photos[side] = { w: img.naturalWidth, h: img.naturalHeight }; updatePreview(); } }),
    h("span", { class: "tag" }, tag(side)),
    ...(logo ? spots.map((spot) => {
      const overlay = movableOverlay(spot, SPOT_LABEL[spot], fitOffset(spot), `${g.tone}${ink}`, logo.previewUrl);
      makeMovable(overlay, spot, side);
      return overlay;
    }) : []));
  return h("div", { id: "preview" },
    s.product === "own" && h("p", { id: "own-preview-note", class: "muted" }, "Preview on a similar garment"),
    list.length > 1 && h("div", { class: "row", role: "group", "aria-label": "Preview color" }, ...list.map((x) =>
      h("button", { id: `show-${x.id}`, type: "button", class: `chip${x.id === g.id ? " on" : ""}`, "aria-pressed": String(x.id === g.id),
        onclick: () => { s.showing = x.id; s.photos = {}; render(); } }, x.color))),
    h("div", { class: `previews${s.on["full-back"] ? " two" : ""}` },
      stage("front", g.photoUrl, FRONT),
      s.on["full-back"] && stage("back", g.backPhotoUrl ?? g.photoUrl, ["full-back"])));
}

// Moves the overlays and updates the size labels and DPI notes without rebuilding the page (sliders call this).
function updatePreview() {
  const g = shown();
  for (const spot of SPOTS) {
    const size = s.on[spot] ? currentSize(spot) : null;
    const label = document.getElementById(`size-${spot}`);
    if (label && size) label.textContent = `${size.widthIn} in wide by ${size.heightIn} in tall`;
    const note = document.getElementById(`dpi-${spot}`);
    if (note && size && s.logo) {
      const { dpi, verdict } = dpiState(s.logo.widthPx, size.widthIn);
      const n = designDpiNotice(dpi, verdict);
      note.className = `notice ${n.cls}`;
      note.textContent = n.text;
    }
    // A wider logo or another color can leave less room in the spot: the logo shows inside it, and the buyer's choice
    // comes back when there is room again.
    const o = fitOffset(spot);
    const limits = spotLimits(picked(), spot);
    showPosition(document, spot, SPOT_LABEL[spot], o, limits && size ? nudgeDisabled(limits, size, o) : null, locked());
    placeOverlay(document.getElementById(`ov-${spot}`), g?.spots[spot], g?.ppi ?? 0, size, s.photos[spot === "full-back" ? "back" : "front"], o);
  }
}

function sizesStep() {
  return section("4. Sizes and quantities",
    ...picked().map((g) => h("div", { class: "color-sizes" },
      h("h3", {}, s.product === "own" ? g.name : g.color),
      h("div", { class: "sizes" }, ...g.sizes.map((size) => {
        const [bank, card] = unitLines(shownUnit(g, size, liveEstimate()));
        return h("label", { class: "size" }, h("span", {}, sizeLabel(size)),
          h("input", { id: `qty-${g.id}-${size}`, type: "number", min: "0", max: "999", step: "1", inputMode: "numeric", placeholder: "0",
            value: s.quantities[g.id]?.[size] ? String(s.quantities[g.id]![size]) : "", disabled: locked(),
            oninput: (e: Event) => setQuantity(g, size, e.target as HTMLInputElement) }),
          h("span", { id: `bank-${g.id}-${size}`, class: "price" }, bank),
          h("span", { id: `card-${g.id}-${size}`, class: "price" }, card));
      })),
      h("p", { id: `count-${g.id}`, class: "muted" }, countText(g)))));
}

function estimatePane() {
  const list = picked();
  const e = liveEstimate();
  const row = (label: string, value: string, cls: string) => h("div", { class: cls }, h("span", {}, label), h("span", {}, value));
  const total = e?.total ?? null;
  const hint = total ? breakHint(e!.addForBreak) : null;
  const missing = colorsMissingQuantities(list, s.quantities);
  // The server's tier lines once its estimate is in, the page's own (worked out the same way) until then.
  const prints = e ? e.prints : s.logo ? printLines(placedSizes(), s.catalog?.printTiers ?? []) : [];
  const waiting = s.estimateState !== "failed" && s.estimateState !== "loading";
  return h("div", { id: "estimate", class: "cart", "aria-live": "polite" },
    h("h2", {}, "Estimate"),
    list.length === 0 && h("p", { class: "muted" }, s.product === "own" ? "Pick your garment type to see prices." : "Pick a style and colors to see prices."),
    list.length > 0 && !s.logo && waiting && h("p", { class: "muted" }, "Upload your artwork to see prices."),
    list.length > 0 && s.logo && !total && waiting && h("p", { class: "muted" }, "Add quantities to see your estimate."),
    s.estimateState === "loading" && h("p", { class: "muted" }, "Updating the estimate..."),
    s.estimateState === "failed" && h("p", { class: "notice warn" }, "The estimate did not load. You can still send your design; we price it when it arrives."),
    list.length > 0 && prints.length > 0 && h("div", { id: "prints" },
      h("p", { class: "muted" }, "Prints, each garment:"),
      ...prints.map((p) => h("div", { class: "print-tier" }, h("span", {}, p.label), h("span", { class: "price" }, unitLines(p)[0]), h("span", { class: "price" }, unitLines(p)[1])))),
    total && e?.blankShipping && blankShippingView(e.blankShipping),
    total && h("div", { class: "totals" },
      row("Garments", String(e!.garmentCount), ""),
      row(BANK_LABEL, money(total.bankCents), "grand"),
      row(CARD_LABEL, money(total.cardCents), "pay")),
    total && e?.discount && discountView(e.discount),
    hint && h("p", { class: "muted" }, hint),
    total && missing.length > 0 && h("p", { class: "notice warn" }, `Add at least one garment for ${listWords(missing)}, or remove ${missing.length === 1 ? "that color" : "those colors"}.`),
    h("p", { class: "muted" }, s.product === "own" ? TRANSFER_CAPTION : ESTIMATE_CAPTION),
    h("p", { id: "turnaround", class: "muted" }, TURNAROUND[s.product]));
}

function productStep() {
  const chip = (p: Product, label: string) => h("button", { id: `product-${p}`, type: "button", class: `chip${s.product === p ? " on" : ""}`,
    "aria-pressed": String(s.product === p), disabled: locked(), onclick: () => pickProduct(p) }, label);
  return section("What are you making?",
    h("div", { class: "row" }, chip("apparel", "Printed apparel"), chip("transfers", "DTF transfers"), chip("own", "Your own garments")),
    h("p", { class: "muted" }, s.product === "transfers"
      ? "Transfers are your artwork printed on film. You press them onto your own garments."
      : s.product === "own"
        ? "Send us your own shirts, hoodies or totes and we print and press them. You pay for the prints only."
        : "We print your artwork on name-brand shirts, hoodies and crewnecks."));
}

// The buyer's own garments: what they send, a color for the preview, and how the garments reach us.
function ownStep() {
  const typeChip = (t: OwnType) => h("button", { id: `own-${t}`, type: "button", class: `chip${s.ownType === t ? " on" : ""}`,
    "aria-pressed": String(s.ownType === t), disabled: locked(), onclick: () => pickOwnType(t) }, OWN_BUTTON[t]);
  return section("1. Your garments",
    h("div", { class: "row", role: "group", "aria-label": "Garment type" }, ...OWN_TYPES.map(typeChip)),
    s.ownType && h("p", { class: "muted" }, "Pick the color closest to yours, for the preview:"),
    s.ownType && h("div", { class: "row own-colors", role: "group", "aria-label": "Preview color" }, ...OWN_COLORS.map((c) =>
      h("button", { id: `own-color-${c.key}`, type: "button", class: `chip swatch-chip${s.ownColor === c.key ? " on" : ""}`, "aria-pressed": String(s.ownColor === c.key),
        title: c.name, disabled: locked(), onclick: () => pickOwnColor(c.key) },
        h("span", { class: "dot", style: `background:${c.hex}` }), c.name))),
    h("details", { class: "own-garments" },
      h("summary", {}, `${GARMENTS_TITLE}: how to get them to us`),
      h("ul", { class: "plain" }, ...garmentsBlock().map((l) => h("li", {}, l)))));
}

function kindStep() {
  const r = film();
  const chip = (k: TransferKind, label: string) => h("button", { id: `kind-${k}`, type: "button", class: `chip${s.kind === k ? " on" : ""}`,
    "aria-pressed": String(s.kind === k), disabled: locked(), onclick: () => pickKind(k) }, label);
  return section("1. Single designs or a gang sheet",
    h("div", { class: "row" }, chip("piece", "Single design"), chip("gang", "Gang sheet")),
    h("p", { class: "muted" }, s.kind === "gang"
      ? `Your own ${r.printerMaxWidthIn} x ${r.maxLengthIn} in layout with several designs on it, printed as one sheet. Upload it ready to print: we print it as it is.`
      : `One design, printed as many times as you need, up to ${r.printerMaxWidthIn} in wide and ${r.maxLengthIn} in long.`));
}

function transferArtStep() {
  const u = s.uploaded;
  const gang = s.kind === "gang";
  const usingCutout = Boolean(u?.cutout && s.logo?.logoId === u.cutout.logoId);
  const shape = gang && s.logo ? gangMessage(s.logo, film()) : null;
  return section("2. Artwork",
    h("label", { class: "drop" },
      s.busy.has("upload") ? "Checking the file..." : gang ? "Upload your gang sheet: PNG, JPG or SVG, up to 25 MB" : "Upload your artwork: PNG, JPG or SVG, up to 25 MB",
      h("input", { id: "logo-file", type: "file", accept: ".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml", disabled: locked(), onchange: onFile })),
    u && h("div", { class: "uploaded" },
      gang ? h("img", { class: "checker", src: u.original.previewUrl, alt: "Your artwork" }) : artworkTile(u, usingCutout),
      !gang && !s.cropping && cutoutChips(u, usingCutout)),
    shape && h("p", { class: "notice bad", "aria-live": "polite" }, shape),
    s.logoError && h("p", { class: "notice bad", "aria-live": "polite" }, s.logoError));
}

function transferSizeStep(logo: ProcessedLogo) {
  const c = s.catalog!;
  const { size, dpi } = shownTransfer()!;
  const note = transferDpiNotice(dpi, s.kind);
  const max = s.kind === "gang" ? c.limits.maxGangSheets : c.limits.maxPieces;
  const b = pieceBounds(logo, film());
  const radio = (f: "pickup" | "ship", label: string) => h("label", { class: "check" },
    h("input", { id: `ship-${f}`, type: "radio", name: "fulfillment", checked: s.fulfillment === f, disabled: locked(), onchange: () => setFulfillment(f) }),
    h("span", {}, label));
  return section("3. Size and quantity",
    s.kind === "piece"
      ? h("label", { class: "field" },
          h("span", { id: "transfer-size" }, `${size.widthIn} in wide by ${size.heightIn} in tall`),
          h("input", { id: "transfer-width", type: "range", min: String(b.min), max: String(b.max), step: String(SLIDER_STEP), value: String(s.pieceWidth),
            disabled: locked(), "aria-labelledby": "transfer-size",
            oninput: (e: Event) => { s.pieceWidth = Number((e.target as HTMLInputElement).value); changed(); updateTransferPreview(); scheduleEstimate(); } }))
      : h("p", { id: "transfer-size" }, `${size.widthIn} in wide by ${size.heightIn} in tall`),
    // A gang sheet in the wrong shape has its own message above; a DPI note under it would only muddy what to fix.
    h("p", { id: "transfer-dpi", class: `notice ${note.cls}`, "aria-live": "polite", hidden: wrongShape() }, note.text),
    h("label", { class: "field" },
      h("span", {}, s.kind === "gang" ? `Gang sheets (1 to ${max})` : `Transfers (1 to ${max})`),
      h("input", { id: "transfer-qty", type: "number", min: "1", max: String(max), step: "1", inputMode: "numeric", placeholder: "0",
        value: s.transferQty ? String(s.transferQty) : "", disabled: locked(),
        oninput: (e: Event) => { s.transferQty = clampQuantity(e.target as HTMLInputElement, max); changed(); scheduleEstimate(); } })),
    h("div", { class: "row", role: "radiogroup", "aria-label": "Pickup or shipping" },
      radio("pickup", pickupLabel("Pickup in Altoona (free)", " (free)")), radio("ship", "Ship it to me")));
}

function transferPreviewPane() {
  const logo = s.logo;
  const size = currentTransfer();
  if (!logo || !size) return h("div", { id: "preview", class: "stage empty" }, "Upload your artwork to see it at its printed size.");
  return h("div", { id: "preview", class: "transfer-stage" },
    h("div", { id: "transfer-box", class: "transfer-box" }, h("img", { src: logo.previewUrl, alt: "Your artwork at its printed size" })),
    h("p", { id: "transfer-label", class: "muted" }, `${size.widthIn} x ${size.heightIn} in, shown on the ${film().printerMaxWidthIn} in film`));
}

// Resizes the preview box and updates the size and DPI lines without rebuilding the page (the slider calls this).
function updateTransferPreview() {
  const shown = shownTransfer();
  if (!shown) return;
  const { size } = shown;
  const box = document.getElementById("transfer-box");
  if (box) {
    box.style.setProperty("--w", String(size.widthIn / film().printerMaxWidthIn));
    box.style.aspectRatio = `${size.widthIn} / ${size.heightIn}`;
  }
  const text = `${size.widthIn} in wide by ${size.heightIn} in tall`;
  const sizeLine = document.getElementById("transfer-size");
  if (sizeLine) sizeLine.textContent = text;
  const label = document.getElementById("transfer-label");
  if (label) label.textContent = `${size.widthIn} x ${size.heightIn} in, shown on the ${film().printerMaxWidthIn} in film`;
  const note = transferDpiNotice(shown.dpi, s.kind);
  const dpi = document.getElementById("transfer-dpi");
  if (dpi) {
    dpi.className = `notice ${note.cls}`;
    dpi.textContent = note.text;
    dpi.hidden = wrongShape();
  }
  refreshSend();
}

function transferEstimatePane() {
  // While a new estimate loads the old totals are hidden, so a price never shows for an order that changed.
  const e = s.estimateState === "loading" ? null : s.transferEstimate;
  const row = (label: string, value: string, cls: string) => h("div", { class: cls }, h("span", {}, label), h("span", {}, value));
  const hint = e ? transferBreakHint(e.addForBreak, s.kind) : null;
  const blocked = transferArtBlock();
  return h("div", { id: "estimate", class: "cart", "aria-live": "polite" },
    h("h2", {}, "Estimate"),
    !s.logo && h("p", { class: "muted" }, "Upload your artwork to see prices."),
    s.logo && !e && s.estimateState === "idle" && h("p", { class: "muted" }, blocked ? `${blocked} to see your estimate.` : "Enter a quantity to see your estimate."),
    s.estimateState === "loading" && h("p", { class: "muted" }, "Updating the estimate..."),
    s.estimateState === "failed" && h("p", { class: "notice warn" }, "The estimate did not load. You can still send your order; we price it when it arrives."),
    e && h("div", { class: "totals" },
      row(s.kind === "gang" ? "Gang sheets" : "Transfers", String(e.quantity), ""),
      row("Each", unitLines(e.unit).join(", "), ""),
      row(BANK_LABEL, money(e.total.bankCents), "grand"),
      row(CARD_LABEL, money(e.total.cardCents), "pay")),
    e?.discount && discountView(e.discount),
    e && h("p", { class: "muted" }, fulfillmentText(e.shippingIncluded ? "ship" : "pickup")),
    hint && h("p", { class: "muted" }, hint),
    h("p", { class: "muted" }, TRANSFER_CAPTION),
    h("p", { id: "turnaround", class: "muted" }, TURNAROUND.transfers));
}

// A field with an error points at it; typing in the field takes the error away without a rebuild.
const errorProps = (key: keyof Contact) => (s.formErrors[key] ? { "aria-invalid": "true", "aria-describedby": `err-${key}` } : {});
function clearError(key: keyof Contact, input: HTMLElement) {
  if (!s.formErrors[key]) return;
  delete s.formErrors[key];
  input.removeAttribute("aria-invalid");
  input.removeAttribute("aria-describedby");
  document.getElementById(`err-${key}`)?.remove();
}

function contactStep() {
  const field = (label: string, key: Exclude<keyof Contact, "note">, type: string, autocomplete: string) =>
    h("label", { class: "field" }, h("span", {}, label),
      h("input", { id: `contact-${key}`, type, autocomplete, maxLength: CONTACT_LIMITS[key], value: s.form[key], disabled: locked(), ...errorProps(key),
        oninput: (e: Event) => { s.form[key] = (e.target as HTMLInputElement).value; clearError(key, e.target as HTMLElement); changed(); } }),
      s.formErrors[key] && h("span", { id: `err-${key}`, class: "err", role: "alert" }, s.formErrors[key]));
  return h("form", { class: "step", novalidate: "", onsubmit: send },
    h("h2", {}, s.product === "transfers" ? "4. Your details" : "5. Your details"),
    field("Name", "name", "text", "name"),
    field("Email", "email", "email", "email"),
    field("Phone (optional)", "phone", "tel", "tel"),
    field("Business (optional)", "business", "text", "organization"),
    h("label", { class: "field" }, h("span", {}, "Anything we should know? (optional)"),
      h("textarea", { id: "contact-note", maxLength: CONTACT_LIMITS.note, rows: 3, value: s.form.note, disabled: locked(), ...errorProps("note"),
        oninput: (e: Event) => { s.form.note = (e.target as HTMLTextAreaElement).value; clearError("note", e.target as HTMLElement); changed(); } }),
      s.formErrors.note && h("span", { id: "err-note", class: "err", role: "alert" }, s.formErrors.note)),
    // People and screen readers never meet this field; a form-filling bot does, and the server ignores a request that
    // fills it. Its name and label are nothing a browser's autofill recognizes, so a real buyer's browser leaves it empty.
    h("div", { class: "hp", "aria-hidden": "true" },
      h("label", {}, "Leave this empty", h("input", { id: "pr_check", name: "pr_check", type: "text", tabIndex: -1, autocomplete: "off", value: s.honeypot,
        oninput: (e: Event) => { s.honeypot = (e.target as HTMLInputElement).value; } }))),
    s.sendError && h("p", { id: "send-error", class: "notice bad", role: "alert" }, s.sendError),
    h("p", { id: "send-hint", class: "muted", hidden: ready() }, sendHint() ?? ""),
    h("button", { id: "send", type: "submit", class: "primary", disabled: locked() || !ready() }, s.busy.has("send") ? "Sending..." : `Send to ${brandName()}`),
    h("p", { class: "muted" }, "Nothing is charged and nothing prints until we confirm with you."));
}

function sentView(r: Sent) {
  const transfers = s.product === "transfers";
  const row = (label: string, value: string, cls: string) => h("div", { class: cls }, h("span", {}, label), h("span", {}, value));
  const heading = h("h2", { id: "sent-title", tabIndex: -1 }, "Sent");
  // No token: the server kept nothing (the hidden field was filled), so there is no link and no price to show.
  if (!r.token) {
    return h("section", { class: "step sent" }, heading,
      h("p", {}, "Thanks. We will be in touch."),
      h("button", { id: "start-over", type: "button", class: "link", onclick: () => location.reload() }, "Start another design"));
  }
  return h("section", { class: "step sent" },
    heading,
    h("p", {}, `Thanks. Your ${transfers ? "transfer order" : "design"} is with ${brandName()}. We will look it over and reply with any questions and a final price.`),
    h("p", { class: "muted" }, r.emailed ? `We emailed a copy to ${s.form.email.trim()}.` : "If the email copy does not arrive, use this link to come back to it."),
    r.shareUrl && h("div", { class: "share" },
      h("input", { id: "share-url", type: "text", readOnly: true, value: r.shareUrl, "aria-label": "Link to your request" }),
      h("button", { id: "copy-link", type: "button", class: "chip", onclick: () => void copyLink() }, s.copied ? "Copied" : "Copy link")),
    r.orderable && r.token && h("a", {
      id: "pay-now", class: "primary", href: `/checkout/${encodeURIComponent(r.token)}`,
      // The buyer's own details travel to the checkout in this tab only; the share link never carries them.
      onclick: () => {
        let store: Storage | null = null;
        try { store = sessionStorage; } catch { store = null; }
        savePrefill(store, r.token!, s.form);
      },
    }, "Pay and order now"),
    r.shareUrl && h("a", { class: r.orderable ? "link" : "primary", href: r.shareUrl }, transfers ? "View your transfers" : "View your design"),
    r.positions && r.positions.length > 0 && h("ul", { id: "sent-positions", class: "plain" }, ...r.positions.map((line) => h("li", {}, line))),
    h("div", { class: "totals" },
      row(BANK_LABEL, money(r.estimate.bankCents), "grand"),
      row(CARD_LABEL, money(r.estimate.cardCents), "pay")),
    h("p", { class: "muted" }, transfers || s.product === "own" ? TRANSFER_CAPTION : ESTIMATE_CAPTION),
    h("p", { id: "sent-turnaround", class: "muted" }, TURNAROUND[s.product]),
    s.product === "own" && h("h3", {}, GARMENTS_TITLE),
    s.product === "own" && h("ul", { id: "sent-garments", class: "plain" }, ...garmentsBlock().map((l) => h("li", {}, l))),
    h("button", { id: "start-over", type: "button", class: "link", onclick: () => location.reload() }, "Start another design"));
}

// mount() rebuilds the page, so the focused control is found again by its id afterwards. A control disabled while a
// request runs gets focus back on the first render after it is enabled again, unless focus moved meanwhile.
let pendingFocus: string | null = null;
function render() {
  if (s.sent) {
    mount(app, sentView(s.sent));
    return;
  }
  const c = s.catalog!;
  const narrow = matchMedia(NARROW).matches;
  const active = document.activeElement;
  const focusId = active && active !== document.body && app.contains(active) && active.id ? active.id : pendingFocus;
  const list = picked();
  const transfers = s.product === "transfers";
  const steps = transfers
    ? [kindStep(), transferArtStep(), s.logo && currentTransfer() && transferSizeStep(s.logo), narrow && transferPreviewPane(), narrow && transferEstimatePane()]
    : [s.product === "own" ? ownStep() : styleStep(c), list.length > 0 && artStep(), list.length > 0 && s.logo && placeStep(), narrow && previewPane(), list.length > 0 && sizesStep(), narrow && estimatePane()];
  // On phones the preview sits under the steps that change it, and the estimate under the quantities.
  mount(app, h("div", { class: "layout" },
    h("div", {},
      h("p", { class: "intro" }, "Make printed apparel or DTF transfers, or have us print your own garments: add your artwork, see it at size, and send it to us. We reply with a final price. Nothing is charged here."),
      productStep(),
      ...steps,
      contactStep()),
    !narrow && h("aside", { class: "side" }, ...(transfers ? [transferPreviewPane(), transferEstimatePane()] : [previewPane(), estimatePane()]))));
  if (transfers) updateTransferPreview();
  else {
    updatePreview();
    applyColorFilter(document, s.filter);
    revealChosenChip(document);
  }
  pendingFocus = null;
  const el = focusId ? (document.getElementById(focusId) as HTMLInputElement | HTMLButtonElement | null) : null;
  if (el && !el.disabled) el.focus({ preventScroll: true });
  else if (el) pendingFocus = focusId;
}

// The designer is not set up: how to reach the storefront, and its own site (brand homeUrl) as a link.
function notReady() {
  const home = homeLink();
  const reach = contactPhrase();
  if (!reach && !home) return h("p", { class: "muted" }, "The designer is being set up. Try again in a moment.");
  const lead = reach ? `${reach[0]!.toUpperCase()}${reach.slice(1)}` : "Use the request form at";
  return h("p", { class: "muted" }, `The designer is being set up. ${lead}`,
    ...(home ? [reach ? " or use the request form at " : " ", h("a", { href: home.href }, home.label)] : []), ".");
}

async function start() {
  // The storefront new requests go to (public_shop), asked alongside the catalog; a failure leaves Bag Carriers.
  const brand = loadBrand("for=design");
  try {
    await loadCatalog();
    await brand;
  } catch (err) {
    mount(app, h("p", { class: "notice bad" }, message(err)));
    return;
  }
  matchMedia(NARROW).addEventListener("change", () => render());
  render();
}

void start();
